import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Banknote, CalendarClock, CheckCircle2 } from "lucide-react";
import { getPayoutStatus, type PayoutRow, type PayoutStatus } from "@/lib/payouts.functions";
import { getStripeEnvironment, paymentsConfigured } from "@/lib/stripe";

export const Route = createFileRoute("/_authenticated/admin/payouts")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewMoney")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Payouts — Coffman Cove Lodging Admin" },
      { name: "description", content: "Account status and upcoming payout schedule for card payments." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPayouts,
});

function money(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function when(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysUntil(value: string | null) {
  if (!value) return null;
  const diff = Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000);
  if (diff <= 0) return "today";
  if (diff === 1) return "tomorrow";
  return `in ${diff} days`;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h2 className="font-display text-lg">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function PayoutList({ rows }: { rows: PayoutRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No payouts yet.</p>;
  }
  return (
    <ul className="divide-y divide-border text-sm">
      {rows.map((p) => (
        <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
          <span className="font-semibold">{money(p.amount, p.currency)}</span>
          <span className="text-muted-foreground">{when(p.arrival_date)}</span>
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{p.status}</span>
        </li>
      ))}
    </ul>
  );
}

function AdminPayouts() {
  const configured = paymentsConfigured();
  const fetchStatus = useServerFn(getPayoutStatus);

  const { data, isLoading, error } = useQuery<PayoutStatus | { error: string }>({
    queryKey: ["admin-payouts"],
    enabled: configured,
    queryFn: () => fetchStatus({ data: { environment: getStripeEnvironment() } }),
    refetchOnWindowFocus: false,
  });

  if (!configured) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <h1 className="font-display text-3xl">Payouts</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Card payments are not connected for this build yet, so there is no payout
          information to show.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <div className="flex items-center gap-2 text-[var(--color-ember)]">
        <Banknote size={18} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-[0.25em]">Money out</span>
      </div>
      <h1 className="mt-2 font-display text-3xl">Payouts</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Account status and when money from card payments lands in the bank account.
      </p>

      {isLoading && <p className="mt-8 text-sm text-muted-foreground">Loading payout details…</p>}

      {(error || (data && "error" in data)) && (
        <div className="mt-8 flex items-start gap-2 rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            {data && "error" in data ? data.error : "Could not load payout details right now."}
          </span>
        </div>
      )}

      {data && !("error" in data) && (
        <div className="mt-8 grid gap-5">
          <Card title="Next payout">
            {data.next_payout ? (
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-display text-3xl">
                  {money(data.next_payout.amount, data.next_payout.currency)}
                </span>
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <CalendarClock size={14} aria-hidden />
                  {when(data.next_payout.arrival_date)}
                  {daysUntil(data.next_payout.arrival_date)
                    ? ` (${daysUntil(data.next_payout.arrival_date)})`
                    : ""}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {data.next_payout.status.replace("_", " ")}
                </span>
              </div>
            ) : data.scheduled_next ? (
              <div>
                <p className="flex flex-wrap items-center gap-2 text-sm">
                  <CalendarClock size={14} aria-hidden />
                  <span className="font-semibold">{when(data.scheduled_next.date)}</span>
                  <span className="text-muted-foreground">
                    {daysUntil(data.scheduled_next.date)
                      ? `(${daysUntil(data.scheduled_next.date)})`
                      : ""}
                  </span>
                  {data.scheduled_next.days_until != null &&
                  data.scheduled_next.days_until <= 3 ? (
                    <span className="rounded-full bg-[var(--color-ember)]/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-ember)]">
                      Within 3 days
                    </span>
                  ) : null}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Nothing has been sent yet — this is your next bank payout date
                  {data.account.schedule_label ? `, ${data.account.schedule_label}` : ""}
                  {data.account.payout_delay_days
                    ? `, about ${data.account.payout_delay_days} days after a sale clears`
                    : ""}
                  . The three-day heads-up email runs off this same date.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No payout is on the way right now
                {data.account.schedule_label
                  ? `, and payouts are sent ${data.account.schedule_label}`
                  : " and no payout schedule is set on the account yet"}
                .
              </p>
            )}
          </Card>

          <Card title="Account status">
            <dl className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Account</dt>
                <dd>{data.account.business_name ?? data.account.id}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Mode</dt>
                <dd className="capitalize">{data.environment === "live" ? "Live" : "Test mode"}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Taking payments</dt>
                <dd className="flex items-center gap-1">
                  {data.account.charges_enabled ? (
                    <>
                      <CheckCircle2 size={14} aria-hidden /> Yes
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={14} aria-hidden /> Not yet
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Payouts enabled</dt>
                <dd className="flex items-center gap-1">
                  {data.account.payouts_enabled ? (
                    <>
                      <CheckCircle2 size={14} aria-hidden /> Yes
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={14} aria-hidden /> Not yet
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Schedule</dt>
                <dd>
                  {data.account.payout_interval
                    ? `${data.account.payout_interval}${
                        data.account.payout_delay_days
                          ? ` (${data.account.payout_delay_days}-day delay)`
                          : ""
                      }`
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Country / currency</dt>
                <dd>
                  {data.account.country ?? "—"} ·{" "}
                  {(data.account.default_currency ?? "").toUpperCase() || "—"}
                </dd>
              </div>
            </dl>

            {data.account.requirements_due.length > 0 && (
              <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <p className="font-semibold">Information still needed before payouts can be sent:</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {data.account.requirements_due.map((r) => (
                    <li key={r}>{r.replace(/[._]/g, " ")}</li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card title="Balance">
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Available to pay out</p>
                {data.balance.available.length === 0 ? (
                  <p>—</p>
                ) : (
                  data.balance.available.map((b) => (
                    <p key={`a-${b.currency}`} className="font-display text-2xl">
                      {money(b.amount, b.currency)}
                    </p>
                  ))
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending (still clearing)</p>
                {data.balance.pending.length === 0 ? (
                  <p>—</p>
                ) : (
                  data.balance.pending.map((b) => (
                    <p key={`p-${b.currency}`} className="font-display text-2xl">
                      {money(b.amount, b.currency)}
                    </p>
                  ))
                )}
              </div>
            </div>
          </Card>

          <Card title="Recent payouts">
            <PayoutList rows={data.recent_payouts} />
          </Card>
        </div>
      )}
    </div>
  );
}
